import pick from 'lodash/pick';
import React, { useMemo } from 'react';
import GooglePlacesAutocomplete, { geocodeByPlaceId } from 'react-google-places-autocomplete';
import { useTranslation } from 'react-i18next';
import FieldContainer from '../../components/FieldContainer';
import StringSummary from '../../components/StringSummary';
import { hasOriginalData } from '../../utils';
import { GooglePlace } from '../CarbonFootprintTransportField/carbon-footprint.types';
import { FieldProps } from '../field.types';
import { getPlaceInfos } from './utils';

type GeoFieldProps = FieldProps<Record<string, any>> & {
  options?: {
    country?: string;
    type?: 'city' | 'station' | 'address';
  };
  strings?: {
    emptyResults?: string;
  };
};

function getAutocompletionTypes(mode?: string): string[] {
  switch (mode) {
    case 'city':
      return ['(cities)'];
    case 'station':
      return ['airport', 'train_station', 'subway_station', 'bus_station', 'transit_station'];
    case 'address':
    default:
      return ['geocode'];
  }
}

export const GeoField = (props: GeoFieldProps) => {
  const { t } = useTranslation();
  const { name, required, value, options, onChange, placeholder, mode, disabled, locked, strings } = props;
  const { country, type } = options || {};
  const autocompletionTypes = useMemo(() => getAutocompletionTypes(type), [type]);

  const handleChange = async (place: { value: GooglePlace } | null) => {
    if (!place || !place.value?.place_id) {
      onChange(name, null);
      return;
    }
    const geo = await geocodeByPlaceId(place.value.place_id);
    const standardPlace = pick(place.value, ['description', 'place_id', 'reference']);
    onChange(name, geo.length > 0 ? getPlaceInfos(geo[0], standardPlace) : standardPlace);
  };

  if (mode === 'summary') {
    return <StringSummary {...props} value={value?.description} type="StringField" />;
  }

  return (
    <FieldContainer {...props} type="GeoField">
      <GooglePlacesAutocomplete
        apiOptions={{ language: 'fr', region: 'fr' }}
        autocompletionRequest={{ types: autocompletionTypes, componentRestrictions: country ? { country: country.toLowerCase() } : undefined }}
        selectProps={{
          required,
          isDisabled: disabled || (locked && hasOriginalData(name)),
          name,
          noOptionsMessage: () => {
            return strings?.emptyResults || t('geo.no-options');
          },
          value: value ? { label: value.description, value: value } : undefined,
          placeholder: placeholder || t('geo.trip-placeholder'),
          isClearable: true,
          onChange: handleChange as any,
        }}
      />
    </FieldContainer>
  );
};
